import React, { createContext, useContext, useState, useCallback } from 'react';
import { View, Platform } from 'react-native';
import { Toast, ToastContextValue, ToastPosition } from '../../shared/types/toast';
import { Toast as ToastComponent } from './Toast';
import { useStyles } from '../../hooks/useStyles';

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const styles = useStyles();

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: Toast = {
      ...toast,
      id,
      position: toast.position ?? 'top',
    };
    
    setToasts((prev) => [...prev, newToast]);
  }, []);

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const hideAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const showSuccess = useCallback(
    (message: string, title?: string) => {
      showToast({ message, title, type: 'success' });
    },
    [showToast]
  );

  const showError = useCallback(
    (message: string, title?: string) => {
      showToast({ message, title, type: 'error' });
    },
    [showToast]
  );

  const showInfo = useCallback(
    (message: string, title?: string) => {
      showToast({ message, title, type: 'info' });
    },
    [showToast]
  );

  const showWarning = useCallback(
    (message: string, title?: string) => {
      showToast({ message, title, type: 'warning' });
    },
    [showToast]
  );

  // Group toasts by position
  const topToasts = toasts.filter((t) => t.position === 'top');
  const bottomToasts = toasts.filter((t) => t.position === 'bottom');

  return (
    <ToastContext.Provider
      value={{
        showToast,
        showSuccess,
        showError,
        showInfo,
        showWarning,
        hideToast,
        hideAllToasts,
      }}
    >
      {children}
      
      {/* Top Toasts */}
      {topToasts.length > 0 && (
        <View
          style={{
            position: 'absolute',
            top: Platform.OS === 'web' ? 16 : 50, // Account for status bar on mobile
            left: 0,
            right: 0,
            zIndex: 9999,
            pointerEvents: 'box-none',
          }}
        >
          <View
            style={{
              paddingHorizontal: styles.utils.spacing.lg,
              maxWidth: 500,
              alignSelf: 'center',
              width: '100%',
            }}
          >
            {topToasts.map((toast) => (
              <ToastComponent
                key={toast.id}
                toast={toast}
                onDismiss={hideToast}
                position="top"
              />
            ))}
          </View>
        </View>
      )}

      {/* Bottom Toasts */}
      {bottomToasts.length > 0 && (
        <View
          style={{
            position: 'absolute',
            bottom: Platform.OS === 'web' ? 16 : 20,
            left: 0,
            right: 0,
            zIndex: 9999,
            pointerEvents: 'box-none',
          }}
        >
          <View
            style={{
              paddingHorizontal: styles.utils.spacing.lg,
              maxWidth: 500,
              alignSelf: 'center',
              width: '100%',
            }}
          >
            {bottomToasts.map((toast) => (
              <ToastComponent
                key={toast.id}
                toast={toast}
                onDismiss={hideToast}
                position="bottom"
              />
            ))}
          </View>
        </View>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}
